/**
 * kingspark-app meal plan callables for the public online-order SPA.
 */

import { httpsCallable } from 'firebase/functions';
import { getCustomerAppFunctions } from './customerAppFirebase';
import { getCustomerAppIdToken } from './customerAppAuth';

export type OnlineMealPlanType = {
  id: string;
  name: string;
  description: string;
  priceCents: number;
  bookingMode: string;
  periods: string[];
  daysOfWeek: number[];
  tcVersionRef: string;
};

export type OnlineMealPlanSubscription = {
  id: string;
  planTypeId: string;
  planTypeName: string;
  status: string;
  activeFrom: number;
};

export type OnlineMealPlanBooking = {
  id: string;
  weekId: string;
  dayKey: string;
  period: string;
  status: string;
};

export type OnlineMealPlanDay = {
  dayKey: string;
  title: string;
  breakfastText: string;
  lunchText: string;
  dinnerText: string;
};

export type OnlineMealPlanWeek = {
  id: string;
  status: string;
  /** Null means every meal that has text. A list is only this week. */
  periods: string[] | null;
  days: OnlineMealPlanDay[];
};

export type OnlineMealPlanStatus = {
  studentVerified: boolean;
  subscription: OnlineMealPlanSubscription | null;
  planTypes: OnlineMealPlanType[];
  currentWeekId: string;
  nextWeekId: string;
  nextWeek: OnlineMealPlanWeek | null;
  bookings: OnlineMealPlanBooking[];
  acceptedTcVersion: string | null;
  tcVersionNeeded: string | null;
  tcBody: string;
  cutoffMs: number;
  bookingMode: string | null;
  canCancelNextWeek: boolean;
};

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function asNumber(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function periodTextFromDay(day: Record<string, unknown>, period: 'breakfast' | 'lunch' | 'dinner'): string {
  const written = day[`${period}Text`];
  if (typeof written === 'string' && written.trim()) return written.trim();
  const rows = Array.isArray(day[period]) ? day[period] : [];
  return rows
    .map((row) => {
      const item = row as Record<string, unknown>;
      return typeof item?.name === 'string' ? item.name.trim() : '';
    })
    .filter(Boolean)
    .join(', ');
}

function parseWeek(raw: unknown): OnlineMealPlanWeek | null {
  if (!raw || typeof raw !== 'object') return null;
  const week = raw as Record<string, unknown>;
  const id = asString(week.id);
  if (!id) return null;
  const menu = week.menu && typeof week.menu === 'object' ? (week.menu as Record<string, unknown>) : {};
  const days = Object.entries(menu)
    .map(([dayKey, value]) => {
      const day = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      return {
        dayKey,
        title: asString(day.title).trim(),
        breakfastText: periodTextFromDay(day, 'breakfast'),
        lunchText: periodTextFromDay(day, 'lunch'),
        dinnerText: periodTextFromDay(day, 'dinner'),
      };
    })
    .filter((day) => day.title || day.breakfastText || day.lunchText || day.dinnerText)
    .sort((a, b) => a.dayKey.localeCompare(b.dayKey));
  const rawPeriods = Array.isArray(week.periods) ? week.periods.map((row) => String(row)) : [];
  return {
    id,
    status: asString(week.status),
    periods: rawPeriods.length ? rawPeriods : null,
    days,
  };
}

function parseStatus(raw: Record<string, unknown>): OnlineMealPlanStatus {
  const planTypesRaw = Array.isArray(raw.planTypes) ? raw.planTypes : [];
  const planTypes: OnlineMealPlanType[] = planTypesRaw
    .map((row) => {
      const p = row as Record<string, unknown>;
      const id = asString(p.id);
      if (!id) return null;
      return {
        id,
        name: asString(p.name),
        description: asString(p.description),
        priceCents: asNumber(p.priceCents),
        bookingMode: asString(p.bookingMode, 'auto'),
        periods: Array.isArray(p.periods) ? p.periods.map((x) => String(x)) : ['lunch'],
        daysOfWeek: Array.isArray(p.daysOfWeek)
          ? p.daysOfWeek.map((x) => Number(x)).filter((n) => Number.isFinite(n))
          : [1, 2, 3, 4, 5],
        tcVersionRef: asString(p.tcVersionRef, '2026-v1'),
      };
    })
    .filter((x): x is OnlineMealPlanType => Boolean(x));

  const subRaw = raw.subscription as Record<string, unknown> | null | undefined;
  const subscription =
    subRaw && asString(subRaw.id)
      ? {
          id: asString(subRaw.id),
          planTypeId: asString(subRaw.planTypeId),
          planTypeName: asString(subRaw.planTypeName),
          status: asString(subRaw.status),
          activeFrom: asNumber(subRaw.activeFrom),
        }
      : null;

  const bookingsRaw = Array.isArray(raw.bookings) ? raw.bookings : [];
  const bookings: OnlineMealPlanBooking[] = bookingsRaw
    .map((row) => {
      const b = row as Record<string, unknown>;
      const id = asString(b.id);
      if (!id) return null;
      return {
        id,
        weekId: asString(b.weekId),
        dayKey: asString(b.dayKey),
        period: asString(b.period),
        status: asString(b.status),
      };
    })
    .filter((x): x is OnlineMealPlanBooking => Boolean(x));

  return {
    studentVerified: Boolean(raw.studentVerified),
    subscription,
    planTypes,
    currentWeekId: asString(raw.currentWeekId),
    nextWeekId: asString(raw.nextWeekId),
    nextWeek: parseWeek(raw.nextWeek),
    bookings,
    acceptedTcVersion: typeof raw.acceptedTcVersion === 'string' ? raw.acceptedTcVersion : null,
    tcVersionNeeded: typeof raw.tcVersionNeeded === 'string' ? raw.tcVersionNeeded : null,
    tcBody: asString(raw.tcBody),
    cutoffMs: asNumber(raw.cutoffMs),
    bookingMode: typeof raw.bookingMode === 'string' ? raw.bookingMode : null,
    canCancelNextWeek: Boolean(raw.canCancelNextWeek),
  };
}

async function requireSignedIn(): Promise<void> {
  const token = await getCustomerAppIdToken(true);
  if (!token) throw new Error('Sign in to manage meal plans.');
}

export async function fetchOnlineMealPlanStatus(): Promise<OnlineMealPlanStatus> {
  await requireSignedIn();
  const fn = httpsCallable(getCustomerAppFunctions(), 'fetchMealPlanStatus');
  const res = await fn({});
  return parseStatus((res.data || {}) as Record<string, unknown>);
}

export async function purchaseOnlineMealPlan(params: {
  planTypeId: string;
  acceptedTcVersion: string;
  returnOrigin?: string;
}): Promise<{ checkoutUrl: string; purchaseId: string; targetWeekId: string; amountCents: number }> {
  await requireSignedIn();
  const fn = httpsCallable(getCustomerAppFunctions(), 'purchaseMealPlan');
  const res = await fn({
    planTypeId: params.planTypeId,
    acceptedTcVersion: params.acceptedTcVersion,
    returnOrigin: params.returnOrigin || (typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname || '/'}`
      : undefined),
  });
  const data = (res.data || {}) as Record<string, unknown>;
  const checkoutUrl = asString(data.checkoutUrl) || asString(data.hppUrl);
  if (!checkoutUrl) throw new Error('Invalid payment session response.');
  return {
    checkoutUrl,
    purchaseId: asString(data.purchaseId),
    targetWeekId: asString(data.targetWeekId),
    amountCents: asNumber(data.amountCents),
  };
}

export async function cancelOnlineMealPlanSubscription(subscriptionId: string): Promise<{
  refund: {
    attempted: boolean;
    success: boolean;
    amountCents?: number;
    refundTxnId?: string;
    skippedReason?: string;
  } | null;
}> {
  await requireSignedIn();
  const fn = httpsCallable(getCustomerAppFunctions(), 'cancelMealPlanSubscription');
  const res = await fn({ subscriptionId });
  const data = (res.data || {}) as Record<string, unknown>;
  const refundRaw = data.refund as Record<string, unknown> | null | undefined;
  if (!refundRaw || typeof refundRaw !== 'object') {
    return { refund: null };
  }
  return {
    refund: {
      attempted: Boolean(refundRaw.attempted),
      success: Boolean(refundRaw.success),
      amountCents:
        typeof refundRaw.amountCents === 'number' ? refundRaw.amountCents : undefined,
      refundTxnId:
        typeof refundRaw.refundTxnId === 'string' ? refundRaw.refundTxnId : undefined,
      skippedReason:
        typeof refundRaw.skippedReason === 'string' ? refundRaw.skippedReason : undefined,
    },
  };
}

export async function cancelOnlineMealPlanBooking(bookingId: string): Promise<void> {
  await requireSignedIn();
  const fn = httpsCallable(getCustomerAppFunctions(), 'cancelMealPlanBooking');
  await fn({ bookingId });
}

export function formatMealPlanCents(cents: number): string {
  return `$${(Math.max(0, cents) / 100).toFixed(2)}`;
}

export function weekdayLabel(n: number): string {
  return ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][n] || String(n);
}
